import Vue from 'vue';
import { mapValues } from 'lodash-es';
import { isLinkLocalIPv4, isLinkLocalIPv6 } from '@/common/utils/ip';

import {
  STATUS_ICON_CHECK_O,
  STATUS_ICON_DISABLED,
  STATUS_ICON_WARNING_O,
  STATUS_ICON_LOADING
} from '@/common/utils/statuses';

import { INTERFACE_TYPES, INTERFACE_STATUSES } from '@/common/constants/nodeInterface.constants';
import { TaskState } from '@schema';
import { TaskName } from '@/common/constants/taskNames.constants';
import { IP_V4_SUBNET_REGEXP, IP_V6_CIDR } from '@/common/utils/validate';

export function mapInterface(iface, rootGetters) {
  const isSpeedSlow = !!(iface?.speeds?.max
    && (iface.speeds.max < 1000
      || (iface.speeds.current < iface.speeds.max && iface.speeds.current < 10000)));
  const isHalfDuplex = iface.duplex === 'half';
  const existErrors = iface.errs > 0;
  let status = INTERFACE_STATUSES.OK;
  let statusIcon = STATUS_ICON_CHECK_O;

  if (iface.state === 'down') {
    status = INTERFACE_STATUSES.DISABLED;
    statusIcon = STATUS_ICON_DISABLED;
  } else if (iface.plugged === false) {
    status = INTERFACE_STATUSES.UNPLUGGED;
    statusIcon = STATUS_ICON_DISABLED;
  } else if (isSpeedSlow || isHalfDuplex || existErrors) {
    status = INTERFACE_STATUSES.WARNING;
    statusIcon = STATUS_ICON_WARNING_O;
  }

  if (iface.tasks?.length) {
    statusIcon = STATUS_ICON_LOADING;
  }

  const ipv4Addresses = iface.ipv4 || [];
  const ipv6Addresses = iface.ipv6 || [];
  const ipv4AndIpv6Addresses = [...ipv4Addresses, ...ipv6Addresses];
  const ipv4AddressesWithoutLinkLocal = ipv4Addresses.filter(ipv4 => !isLinkLocalIPv4(ipv4));
  const ipv6AddressesWithoutLinkLocal = ipv6Addresses.filter(ipv6 => !isLinkLocalIPv6(ipv6));
  const firstIpAddress = ipv4AddressesWithoutLinkLocal.length > 0 ? ipv4AddressesWithoutLinkLocal[0] : ipv6AddressesWithoutLinkLocal[0];
  const network = rootGetters['rolesets/getNetworkById'](iface.roles_set);

  return {
    ...iface,
    id: iface.name,
    isSpeedSlow,
    isHalfDuplex,
    existErrors,
    status,
    statusIcon,
    ipv4Addresses,
    ipv6Addresses,
    ipv4AddressesWithoutLinkLocal,
    ipv6AddressesWithoutLinkLocal,
    firstIpAddress,
    network,
    ipv4AndIpv6Addresses
  };
}

export function subnetFormApiMapper(form) {
  let ignoreAutoRoutes;
  let dhcp4Enabled;

  if (form.dhcpMode === 'dhcp') {
    ignoreAutoRoutes = false;
    dhcp4Enabled = true;
  } else if (form.dhcpMode === 'dhcpAddressOnly') {
    ignoreAutoRoutes = true;
    dhcp4Enabled = true;
  } else {
    ignoreAutoRoutes = false;
    dhcp4Enabled = false;
  }

  return {
    gw4: form.gw4 || null,
    gw6: form.gw6 || null,
    ipv6: form.ipv4andIpv6
      .filter(item => IP_V6_CIDR.test(item.value))
      .map(item => item.value),
    dhcp6_enabled: false,
    ignore_auto_routes_v6: ignoreAutoRoutes,
    ignore_auto_routes_v4: ignoreAutoRoutes,
    ipv4: !dhcp4Enabled
      ? form.ipv4andIpv6
        .filter(item => IP_V4_SUBNET_REGEXP.test(item.value))
        .map(item => item.value)
      : [],
    dhcp4_enabled: dhcp4Enabled,
  };
}

export function createFormApiMapper(form) {
  return {
    ...subnetFormApiMapper(form),
    mtu: form.mtu || null,
    name: '',
    roles_set: form.roles_set || null,
  };
}

export function editFormApiMapper(form) {
  return {
    ...subnetFormApiMapper(form),
    mtu: form.mtu || null,
    roles_set: form.roles_set || null,
    connected_mode: form.type === INTERFACE_TYPES.INFINIBAND ? form.connected_mode : undefined
  };
}

export function apiDhcpMode(iface) {
  if (!iface.ignore_auto_routes_v4 && iface.dhcp4_enabled) {
    return 'dhcp';
  } else if (iface.ignore_auto_routes_v4 && iface.dhcp4_enabled) {
    return 'dhcpAddressOnly';
  }
  return 'configureIpManually';
}

export function editApiFormMapper(iface) {
  return {
    id: iface.id,
    type: iface.type,
    roles_set: iface.roles_set || null,
    dhcpMode: apiDhcpMode(iface),
    ipv4andIpv6: [
      ...iface.ipv4Addresses.map(it => ({ value: it })),
      ...iface.ipv6Addresses.map(it => ({ value: it }))
    ],
    gw4: iface.gw4,
    gw6: iface.gw6,
    mtu: iface.mtu,
    connected_mode: iface.connected_mode || null
  };
}

export default {
  name: 'nodesInterfaces',
  namespaced: true,
  state: {
    interfaces: {},
    pendings: {},
  },
  getters: {
    getInterfaces: (state, getters, rootState, rootGetters) => (nodeId) => (state.interfaces[nodeId] || [])
      .map(iface => mapInterface(iface, rootGetters)),
    getInterface: (state, getters, rootState, rootGetters) => (nodeId, interfaceId) => {
      const data = (state.interfaces[nodeId] || []).find(iface => iface.name === interfaceId);
      return data ? mapInterface(data, rootGetters) : undefined;
    },
    getPending: state => nodeId => state.pendings[nodeId] || false,
    isAnyPending: state => Object.values(state.pendings).find((it) => it === true) || false,
    getInterfacesList: (state, getters, rootState, rootGetters) => mapValues(state.interfaces, interfaces => interfaces
      .map(iface => mapInterface(iface, rootGetters)))
  },
  mutations: {
    SET_INTERFACES(state, { nodeId, interfaces }) {
      Vue.set(state.interfaces, nodeId, interfaces);
    },
    SET_PENDING(state, { nodeId, status }) {
      Vue.set(state.pendings, nodeId, status);
    }
  },
  actions: {
    FETCH_INTERFACES({ commit }, { nodeId }) {
      commit('SET_PENDING', { nodeId, status: true });
      return this.$api.get(`/api/v2/nodes/${nodeId}/net/ifs`)
        .then((response) => {
          commit('SET_INTERFACES', { nodeId, interfaces: response.data?.data || [] });
          return response;
        })
        .finally(() => {
          commit('SET_PENDING', { nodeId, status: false });
        });
    },
    CREATE_INTERFACE({ dispatch }, data) {
      if (data.form.type === 'vlan') {
        return dispatch('CREATE_VLAN_INTERFACE', data);
      }
      return dispatch('CREATE_BOND_INTERFACE', data);
    },
    CREATE_VLAN_INTERFACE(store, { nodeId, form }) {
      return this.$api.post(`/api/v2/nodes/${nodeId}/net/vlans`, {
        ...createFormApiMapper(form),
        tag: form.tag,
        iface: form.ifaces[0].id
      });
    },
    CREATE_BOND_INTERFACE(store, { nodeId, form }) {
      const payload = {
        ...createFormApiMapper(form),
        bond_type: form.bond_type,
        ifaces: form.ifaces.map(it => it.id)
      };
      if (form.mac_addr) {
        payload.mac_addr = form.mac_addr;
      }
      return this.$api.post(`/api/v2/nodes/${nodeId}/net/bonds`, payload);
    },
    EDIT_INTERFACE({ dispatch }, { nodeId, interfaceId, form }) {
      return dispatch('UPDATE_INTERFACE', {
        nodeId,
        interfaceName: interfaceId,
        body: {
          ...editFormApiMapper(form)
        }
      });
    },
    UPDATE_INTERFACE(store, payload) {
      return this.$api.patch(`/api/v2/nodes/${payload.nodeId}/net/ifs/${payload.interfaceName}`, payload.body);
    },
    DOWN_INTERFACE(store, { nodeId, interfaceId }) {
      return this.$api.post(`/api/v2/nodes/${nodeId}/net/ifs/${interfaceId}/down/`);
    },
    UP_INTERFACE(store, { nodeId, interfaceId }) {
      return this.$api.post(`/api/v2/nodes/${nodeId}/net/ifs/${interfaceId}/up/`);
    },
    DELETE_INTERFACE(store, { nodeId, interfaceId }) {
      return this.$api.delete(`/api/v2/nodes/${nodeId}/net/ifs/${interfaceId}/`);
    },
    socket_tasks({ dispatch }, task) {
      if (!task.args?.length) return;
      const [nodeId] = task.args;
      const tasksList = [
        TaskName.NETWORK_INTERFACE_VLAN_CREATE,
        TaskName.NETWORK_INTERFACE_BOND_CREATE,
        TaskName.NETWORK_INTERFACE_UPDATE,
        TaskName.NETWORK_INTERFACE_DELETE,
        TaskName.NETWORK_INTERFACE_DOWN,
        TaskName.NETWORK_INTERFACE_UP
      ];
      if (tasksList.includes(task.name) && task.state === TaskState.SUCCESS) {
        dispatch('FETCH_INTERFACES', { nodeId });
      }
    }
  }
};
